import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import WebIcon from '@mui/icons-material/Web';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SchoolIcon from '@mui/icons-material/School';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ArticleIcon from '@mui/icons-material/Article';

const MotionCard = motion(Card);

const projects = [
  {
    title: 'MakeMyResume AI',
    description: 'An AI-powered platform to build and download professional resumes with intelligent suggestions.',
    icon: <ArticleIcon sx={{ fontSize: 60 }} />,
    technologies: ['React.js', 'Next.js', 'AI/ML APIs', 'Material-UI', 'Node.js', 'Vercel'],
    category: 'Web App',
    color: '#14b8a6',
    details: 'A cutting-edge web application that leverages AI to provide users with smart suggestions, professional templates, and an intuitive editor to create and download job-winning resumes in minutes.',
  },
  {
    title: 'FusionPOS Restaurant System',
    description: 'A hybrid local and cloud-based management solution for restaurants on Android & Windows.',
    icon: <PointOfSaleIcon sx={{ fontSize: 60 }} />,
    technologies: ['Kotlin', '.NET', 'Android SDK', 'SQLite', 'AWS', 'REST APIs'],
    category: 'Desktop & Mobile',
    color: '#f97316',
    details: 'A comprehensive restaurant management system designed for flexibility. It operates seamlessly offline on local Android and Windows devices and syncs with a powerful cloud backend for multi-location management, reporting, and analytics. Features include order management, inventory control, and staff scheduling.',
  },
  {
    title: 'Smart Kids Learning',
    description: 'An interactive and engaging educational website to help children learn new skills (in progress).',
    icon: <SchoolIcon sx={{ fontSize: 60 }} />,
    technologies: ['React.js', 'Framer Motion', 'GraphQL', 'Netlify'],
    category: 'Web App',
    color: '#3b82f6',
    details: 'An ongoing project to build a fun and interactive learning platform for kids. The site will feature educational games, videos, and quizzes across various subjects, designed to make learning an exciting adventure.',
  },
  {
    title: 'Clip & Dapper Booking App',
    description: 'A sleek mobile app for customers to easily book appointments at their favorite barbershop.',
    icon: <ContentCutIcon sx={{ fontSize: 60 }} />,
    technologies: ['Swift (iOS)', 'Kotlin (Android)', 'Firebase', 'Google Calendar API'],
    category: 'Mobile App',
    color: '#475569',
    details: 'A user-friendly mobile application that simplifies the booking process for barbershops. Customers can view available slots, choose their preferred barber, book appointments, and receive reminders, all from their phone.',
  },
  {
    title: 'AWS Cloud Consultancy',
    description: 'Expert AWS consulting services to design, deploy, and manage scalable cloud infrastructure.',
    icon: <CloudDoneIcon sx={{ fontSize: 60 }} />,
    technologies: ['AWS', 'Terraform', 'Docker', 'Kubernetes', 'CloudFormation'],
    category: 'Consultancy',
    color: '#0891b2',
    details: 'Providing specialized AWS consultancy to businesses of all sizes. Services include architecture design, migration planning, cost optimization, security audits, and ongoing management to ensure a robust, scalable, and efficient cloud environment.',
  },
  {
    title: 'Enterprise CI/CD & Infrastructure',
    description: 'Designing and implementing robust CI/CD pipelines and infrastructure for large-scale companies.',
    icon: <SyncAltIcon sx={{ fontSize: 60 }} />,
    technologies: [
      'Jenkins',
      'GitLab CI',
      'GitHub Actions',
      'AWS CloudFormation',
      'Terraform',
      'Ansible',
      'Docker',
      'Kubernetes',
    ],
    category: 'DevOps',
    color: '#8b5cf6',
    details: 'Specializing in setting up automated CI/CD pipelines and scalable infrastructure for enterprise-level organizations. This includes implementing best practices for version control, automated testing, containerization, and infrastructure as code (IaC) to accelerate development cycles and improve reliability.',
  },
];

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('All');

  const handleOpenDialog = (project) => {
    setSelectedProject(project);
  };

  const handleCloseDialog = () => {
    setSelectedProject(null);
  };

  const categories = ['All', ...new Set(projects.map(p => p.category))];

  const filteredProjects = projects.filter(
    (project) => filter === 'All' || project.category === filter
  );

  return (
    <>
      <SEOHead
        title="Portfolio - DevHopes Labs Development Projects & Case Studies"
        description="Explore our portfolio of mobile and web development projects including iOS apps, Android apps, React.js web applications, and Shopify solutions. See how we've helped businesses achieve their digital transformation goals."
        keywords="DevHopes Labs portfolio, mobile app projects, web development projects, iOS app examples, Android app showcase, React.js projects, Next.js applications, Shopify development examples, development case studies, mobile app portfolio, web app examples"
        url="/portfolio"
        type="website"
        schemaType="CollectionPage"
      />

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 8, md: 10 },
          mt: { xs: 7, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Portfolio
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ 
                mb: 6, 
                maxWidth: 700, 
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                lineHeight: 1.6,
              }}
            >
              Showcasing our best work in mobile and web development, cloud consultancy, and DevOps.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Filter Section */}
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 1, sm: 1.5, md: 2 },
              flexWrap: 'wrap',
              mb: { xs: 4, md: 6 },
            }}
          >
            {categories.map((category) => (
              <Button
                key={category}
                variant={filter === category ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setFilter(category)}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {category}
              </Button>
            ))}
          </Box>

          {/* Projects Grid */}
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {filteredProjects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => handleOpenDialog(project)}
                >
                  {/* Icon Header instead of image */}
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${project.color} 0%, ${project.color}dd 100%)`,
                      color: 'white',
                    }}
                  >
                    {project.icon}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column' }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ 
                        mb: 3,
                        lineHeight: 1.6,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        flexGrow: 1,
                      }}
                    >
                      {project.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
                      {project.technologies.slice(0, 3).map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          label={tech}
                          size="small"
                          sx={{
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        />
                      ))}
                      {project.technologies.length > 3 && (
                        <Chip
                          label={`+${project.technologies.length - 3}`}
                          size="small"
                          sx={{
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Project Detail Dialog */}
      <Dialog
        open={Boolean(selectedProject)}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        {selectedProject && (
          <>
            <DialogTitle sx={{ p: 3, pb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: selectedProject.color,
                    width: 50,
                    height: 50,
                    color: 'white',
                  }}
                >
                  {React.cloneElement(selectedProject.icon, { sx: { fontSize: 30 } })}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedProject.title}
                  </Typography>
                  <Chip
                    label={selectedProject.category}
                    size="small"
                    sx={{
                      bgcolor: 'primary.50',
                      color: 'primary.main',
                      fontWeight: 500,
                      mt: 1,
                    }}
                  />
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {selectedProject.details}
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Technologies Used:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedProject.technologies.map((tech, index) => (
                  <Chip
                    key={index}
                    label={tech}
                    sx={{
                      bgcolor: 'primary.50',
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default Portfolio; 